import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Page } from '../library/Page';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PageContentCard } from '../library/PageContentCard';
import { PageHeader } from '../library/PageHeader';
import { PricingPublishDialog } from '../PricingPublishDialog';
import { ClientDataBranch } from '../../constants/ClientDataBranch';
import { I18nKeys } from '../../constants/I18nKeys';
import { PricingPublishResultDialog } from '../PricingPublishResultDialog';
import { getClientIdFromClientSupplier } from '../../utils/clientIdUtils';
import { unknownGroup } from '../../constants/Group';
import { PricingCategories } from './PricingCategories';
import { useClientUpdatePricingRepo } from '../../hooks/useClientUpdatePricingRepo';
import { notifyOfDataChange } from '../../utils/pricingUtils';
import { LocalStorage } from '../../constants/LocalStorage';
import { PricingClientUpdatePreviewDialog } from '../PricingClientUpdatePreviewDialog';
import { SizeBasedPricingSheets } from './SizeBasedPricingSheets';
import { filterClientUpdateCategories } from '../../utils/pricingClientUpdateUtils';
import { PricingAttributes } from './PricingAttributes';
import { CLIENT_UPDATE_CATEGORY_MAPPING } from '../../constants/PricingClientUpdate';
import { setSelectedSizeBasedCategoryKey } from '../../ducks/pricingSlice';

export const SizeBasedPrices: React.FC<{ clientId: string }> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { selectedCategoryKey: sizeBasedCategoryKey } = useAppSelector((state) => state.pricing.sizeBased);
  const searchTerm = useAppSelector((state) => state.search.searchTerm);
  const clientId = useAppSelector((state) =>
    getClientIdFromClientSupplier(state?.viewer?.selectedTabId || state?.viewer?.selectedClientId || ''),
  );
  const selectedPricingSheetId = useAppSelector((state) => state.pricing.sizeBased.selectedPricingSheetId);

  const { group: { groupId } = unknownGroup } = useAppSelector((state) => state?.currentUser);

  const {
    clientUpdateBranchMergeCommit,
    clientUpdateCategoriesList = [],
    isLoadingClientUpdateCategoriesList,
    sizeBasedCategoryPricingSheets: pricingSheets = [],
    isLoadingSizeBasedCategoryPricingSheets,
    cellMetadataDiff = [],
    clientUpdateDiffs,
  } = useClientUpdatePricingRepo({
    useClientUpdateBranchMergeCommit: true,
    useClientUpdateCategories: true,
    useSizeBasedCategoryPricingSheets: true,
    useCellMetadataDiff: true,
    useClientUpdateDiffs: true,
  });

  useEffect(() => {
    notifyOfDataChange({
      groupId,
      clientId,
      latestCommit: clientUpdateBranchMergeCommit,
      key: LocalStorage.LastPricingSizeBasedMergeCommit,
      dispatch,
      t,
    });
  }, [clientUpdateBranchMergeCommit, dispatch, t, groupId, clientId]);

  const filteredComponentCategories = filterClientUpdateCategories(searchTerm, clientUpdateCategoriesList, t);

  return (
    <Page>
      <PageHeader
        id="height-walls-header"
        header="Height & Walls"
        section={sizeBasedCategoryKey ? t(CLIENT_UPDATE_CATEGORY_MAPPING[sizeBasedCategoryKey]?.label) : undefined}
        count={sizeBasedCategoryKey ? pricingSheets?.length : filteredComponentCategories?.length}
        countLabel={sizeBasedCategoryKey ? 'sheet' : 'category'}
        loading={sizeBasedCategoryKey ? isLoadingSizeBasedCategoryPricingSheets : isLoadingClientUpdateCategoriesList}
        onHeaderClick={() => dispatch(setSelectedSizeBasedCategoryKey(undefined))}
      />
      <Box display="flex" flex={1} position="relative">
        {!sizeBasedCategoryKey && <PricingCategories />}
        {sizeBasedCategoryKey && (
          <Box display="flex" flex={1} position="relative">
            <PricingAttributes
              // Use pricing sheet from all pricing sheets, so it doesn't wait for the selected pricing sheet to load
              selectedPricingSheet={pricingSheets.find((sheet) => sheet.id === selectedPricingSheetId) || undefined}
              pricingSheets={pricingSheets}
              cellMetadataDiff={cellMetadataDiff}
              pricingDiffs={clientUpdateDiffs}
            />
            <Box
              display="flex"
              flex={1}
              position="relative"
              sx={{
                marginLeft: '263px',
                marginBottom: '-24px',
                paddingBottom: '24px',
                borderLeft: '1px solid #DEDEDE',
              }}
            >
              <PageContentCard
                sx={{
                  display: 'flex',
                  flex: 1,
                  height: 'auto',
                  minHeight: '100%',
                  borderBottomLeftRadius: '0px',
                  borderTopLeftRadius: '0px',
                  borderTop: '1px solid #DEDEDE',
                }}
              >
                <SizeBasedPricingSheets />
              </PageContentCard>
            </Box>
          </Box>
        )}
      </Box>
      <PricingClientUpdatePreviewDialog />
      <PricingPublishDialog
        pricingBranch={ClientDataBranch.Pricing}
        title={I18nKeys.PricingBasePublishDialogTitle}
        content={I18nKeys.PricingComponentPublishDialogText}
      />
      <PricingPublishResultDialog />
    </Page>
  );
};
